import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Flag 2 — re-score nudge. Appears after a material ICP change so existing
// leads can be realigned to the new profile with one tap. Reuses the existing
// /api/leads/rescore-all endpoint.
export const ICPRescoreNudge = ({ icp }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${API}/api/brain/icp/rescore-status`);
      setStatus(r.data || null);
    } catch { setStatus(null); }
  }, []);

  // Re-check on mount and whenever the ICP is saved (updated_at changes).
  useEffect(() => { load(); }, [load, icp?.updated_at]);

  const rescore = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${API}/api/leads/rescore-all`);
      const n = r.data?.queued ?? 0;
      toast.success(`Re-scoring ${n} lead${n === 1 ? '' : 's'} against your updated ICP`);
      setStatus(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start re-scoring');
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post(`${API}/api/brain/icp/rescore-dismiss`);
      setStatus(null);
    } catch { /* no-op */ }
    finally { setBusy(false); }
  };

  if (!status || !status.needs_rescore) return null;

  return (
    <div
      data-testid="icp-rescore-nudge"
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3"
    >
      <div className="flex items-start gap-2.5">
        <div className="p-1.5 rounded-lg bg-amber-500/15 shrink-0">
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">Your ICP changed</p>
          <p className="text-xs text-muted-foreground">
            Re-score <span className="font-medium text-foreground">{status.lead_count?.toLocaleString()}</span> existing
            {' '}lead{status.lead_count === 1 ? '' : 's'} so your pipeline matches the new profile.
          </p>
          {status.audience_excluded > 0 && (
            <p className="text-[11px] text-muted-foreground/80 mt-0.5" data-testid="icp-rescore-audience-note">
              +{status.audience_excluded.toLocaleString()} audience lead{status.audience_excluded === 1 ? '' : 's'} not included
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 self-start sm:self-center">
        <button
          onClick={rescore}
          disabled={busy}
          data-testid="icp-rescore-run-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-score now
        </button>
        <button
          onClick={dismiss}
          disabled={busy}
          data-testid="icp-rescore-dismiss-btn"
          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground"
        >
          <X className="w-3.5 h-3.5" /> Later
        </button>
      </div>
    </div>
  );
};

export default ICPRescoreNudge;
